import { DisplayMode } from '@rocco/types/enum/display-mode';
import { FloatingIsland } from './FloatingIsland';
import { SearchBar } from '@rocco/ui/search/components/SearchBar';
import { useSearchInput } from '@rocco/ui/search/hooks/use-search-input';
import { useRef, useEffect } from 'react';
import { useLocation } from '@remix-run/react';
import { isMobile } from 'react-device-detect';
interface SearchIslandProps {
    input: ReturnType<typeof useSearchInput>;
    placeholderTexts: string[];
    displayMode: DisplayMode;
    autoFocusOnVisible?: boolean;
    onClick?: () => void;
    scrollThreshold?: number;
    onBeforeSearchSubmit?: (query: string) => void;
}

export const SearchIsland = (props: SearchIslandProps) => {
    const {
        input,
        placeholderTexts,
        displayMode = 'desktop',
        autoFocusOnVisible = true,
        onClick,
        scrollThreshold = 200,
        onBeforeSearchSubmit,
    } = props;
    const searchInputRef = useRef<HTMLInputElement>(null);
    const location = useLocation();
    const {
        searchQuery,
        handleInputChange,
        handleKeyPress,
        handleSearch,
        suggestionGroups,
        clearSearchQuery,
    } = input;

    const tryFocusSearchInput = () => {
        // Only focus if the user hasn't explicitly focused something else
        if (
            !document.activeElement ||
            document.activeElement === document.body
        ) {
            if (isMobile) {
                searchInputRef.current?.blur();
            } else {
                searchInputRef.current?.focus();
            }
        }
    };

    const handleVisibilityChange = (isVisible: boolean) => {
        if (isVisible && autoFocusOnVisible && displayMode === 'desktop') {
            tryFocusSearchInput();
        }
    };

    useEffect(() => {
        if (autoFocusOnVisible && displayMode === 'desktop') {
            if (isMobile) {
                searchInputRef.current?.blur();
            } else {
                searchInputRef.current?.focus();
            }
        }
    }, [autoFocusOnVisible, displayMode, location.pathname]);

    const handleClick = () => {
        searchInputRef.current?.focus();
        onClick?.();
    };

    return (
        <FloatingIsland
            variant="category"
            onVisibilityChange={handleVisibilityChange}
            onClick={handleClick}
            scrollThreshold={scrollThreshold}
        >
            <SearchBar
                key="searchIsland-searchBar"
                ref={searchInputRef}
                placeholderTexts={placeholderTexts}
                value={searchQuery}
                onChange={handleInputChange}
                onKeyDown={handleKeyPress}
                onSearchSubmit={(value) => {
                    if (value && typeof value === 'string') {
                        onBeforeSearchSubmit?.(value);
                        handleSearch(value);
                    }
                }}
                suggestionGroups={suggestionGroups}
                clearSearchQuery={clearSearchQuery}
                hideClearButton={true}
                variant="transparent"
                displayMode={displayMode}
            />
        </FloatingIsland>
    );
};
